import {
  type ConsoleClientCredits,
  type ConsoleCreditLimit,
  type ConsoleCreditLimits,
  type ConsoleUsage,
  type ConsoleUsageTotals,
  effectiveCreditLimit,
} from '@plato/shared';
import type { UsageData } from './usage-data.ts';

const CREDIT = 1_000_000;
const DAY_MS = 86_400_000;
const MONTH_START = '2026-10-01T00:00:00.000Z';

const CLIENTS: ReadonlyArray<Omit<ConsoleClientCredits, 'limit'>> = [
  { organization: 'northwind', plan: 'pro', runs: 1_840, credit_micros: 452 * CREDIT },
  { organization: 'contoso', plan: 'pro', runs: 960, credit_micros: 214 * CREDIT },
  { organization: 'fabrikam', plan: 'starter', runs: 410, credit_micros: 46 * CREDIT },
  { organization: null, plan: null, runs: 120, credit_micros: 9 * CREDIT },
];

let limits: ConsoleCreditLimit[] = [
  { kind: 'default', subject: '', monthly_credit_micros: 50 * CREDIT, updated_at: MONTH_START },
  { kind: 'plan', subject: 'pro', monthly_credit_micros: 500 * CREDIT, updated_at: MONTH_START },
];

export const demoUsageData: UsageData = {
  getUsage: async (_tenant, range) => demoUsage(range.from, range.to),
  getCreditLimits: async () => creditLimits(),
  setCreditLimit: async (_tenant, limit) => {
    limits = [
      ...limits.filter((l) => !(l.kind === limit.kind && l.subject === limit.subject)),
      { ...limit, updated_at: new Date().toISOString() },
    ];
    return creditLimits();
  },
  removeCreditLimit: async (_tenant, kind, subject) => {
    limits = limits.filter((l) => !(l.kind === kind && l.subject === subject));
    return creditLimits();
  },
  getOverview: async () => {
    const { clients } = creditLimits();
    return {
      month_start: MONTH_START,
      credit_micros: 731 * CREDIT,
      runs: 3_330,
      conversations: 1_240,
      conversations_rated_up: 212,
      conversations_rated_down: 31,
      conversations_with_errors: 18,
      tool_calls: 9_860,
      failed_tool_calls: 74,
      near_limit_ratio: 0.8,
      clients_near_limit: clients.filter(
        (client) =>
          client.organization !== null &&
          client.limit !== null &&
          client.credit_micros >= 0.8 * client.limit.monthly_credit_micros,
      ),
    };
  },
};

function creditLimits(): ConsoleCreditLimits {
  const comparable = limits.map((limit) => ({
    ...limit,
    monthlyCreditMicros: limit.monthly_credit_micros,
  }));
  const clients = CLIENTS.map((client) => {
    const found = effectiveCreditLimit(comparable, {
      organizationSubject: client.organization,
      plan: client.plan,
    });
    const limit = limits.find((l) => l.kind === found?.kind && l.subject === found.subject);
    return { ...client, limit: limit ?? null };
  });
  return { month_start: MONTH_START, limits: [...limits], clients };
}

function demoUsage(from: string, to: string): ConsoleUsage {
  const days: ConsoleUsage['by_day'] = [];
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  for (let at = start; at <= end; at += DAY_MS) {
    const date = new Date(at);
    const weekend = date.getUTCDay() === 0 || date.getUTCDay() === 6;
    const credits = (weekend ? 6 : 20) + ((date.getUTCDate() * 7) % 11);
    days.push({ day: date.toISOString().slice(0, 10), ...totals(credits) });
  }
  const credits = days.reduce((total, day) => total + day.credit_micros, 0) / CREDIT;
  const share = (part: number) => totals(Math.round(credits * part));
  return {
    from,
    to,
    totals: totals(credits),
    by_day: days,
    by_organization: [
      { key: 'northwind', ...share(0.55) },
      { key: 'contoso', ...share(0.3) },
      { key: 'fabrikam', ...share(0.1) },
      { key: null, ...share(0.05) },
    ],
    by_plan: [
      { key: 'pro', ...share(0.85) },
      { key: 'starter', ...share(0.1) },
      { key: null, ...share(0.05) },
    ],
    by_purpose: [
      { key: 'chat', ...share(0.9) },
      { key: 'eval', ...share(0.08) },
      { key: 'preview', ...share(0.02) },
    ],
  };
}

function totals(credits: number): ConsoleUsageTotals {
  return {
    runs: credits * 4,
    input_tokens: credits * 90_000,
    output_tokens: credits * 6_000,
    cache_read_tokens: credits * 40_000,
    cache_write_tokens: credits * 8_000,
    sandbox_ms: credits * 2_500,
    credit_micros: credits * CREDIT,
    unpriced_runs: 0,
  };
}
